// Pure formatting + date-range helpers for the Stats screen. Dependency-free
// and reusable independently of the heavy aggregation in statsCompute, so they
// live in their own module. Mirrors Android's StatsScreen formulas exactly.

import { RangePreset, DateRange } from "./statsTypes";

const MS_PER_HOUR = 3_600_000;

export const RANGE_PRESETS: RangePreset[] = [
  { key: "LAST_7_DAYS", label: "Last 7 days", days: 7 },
  { key: "LAST_1_MONTH", label: "Last 1 month", days: 30 },
  { key: "LAST_3_MONTHS", label: "Last 3 months", days: 90 },
];

/** Local YYYY-MM-DD key for a Date (groups sessions by their START day). */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** {startDate, endDate} (inclusive) for a preset, mirroring Android:
 *  end = today, start = today - (days-1). */
export function rangeForPreset(days: number, today: Date = new Date()): DateRange {
  const endDate = startOfDay(today);
  const startDate = startOfDay(today);
  startDate.setDate(startDate.getDate() - (days - 1));
  return { startDate, endDate };
}

export function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function costFor(durationMs: number, hourlyRate: number): number {
  return (durationMs / MS_PER_HOUR) * hourlyRate;
}

export function hoursOf(durationMs: number): number {
  return durationMs / MS_PER_HOUR;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function weekdayLabel(date: Date): string {
  return WEEKDAYS[date.getDay()];
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "EEE MMM d" e.g. "Mon Jun 2". */
export function formatDayHeader(date: Date): string {
  return `${weekdayLabel(date)} ${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

export function formatDateLong(date: Date): string {
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Round up to a "nice" y-axis hour value (matches Android's ceilToNiceHours). */
export function niceMaxHours(hours: number): number {
  if (hours <= 0) return 1;
  const nice = [0.5, 1, 2, 4, 8];
  for (const n of nice) if (hours <= n) return n;
  return Math.ceil(hours / 2) * 2;
}
